import { Component, ReactNode, createRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import uniqid from 'uniqid';

const styles = (theme: Theme) =>
    createStyles({
        container: {
            whiteSpace: 'nowrap',
            position: 'relative',
            display: 'inline-block',
            border: '1px solid ' + theme.status.default.backgroundColor,
            // Matches PaloSegmentedToggle, which sits next to this on the same
            // masks — a full pill read as a different control.
            borderRadius: 6,
            backgroundColor: theme.status.default.backgroundColor,
            height: 32,
        },
        flexContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        swapper: {
            position: 'absolute',
            borderRadius: 4,
            height: '100%',
            top: 0,
            left: 0,
            backgroundColor: theme.palette.primary.main,
            transition: theme.transitions.create('transform', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.short,
            }),
        },
        content: {
            position: 'relative',
            display: 'inline-block',
            zIndex: 1,
            flexGrow: 1,
            fontFamily: theme.typography.fontFamily,
            fontSize: '12.5px',
            fontWeight: 560,

            '& input': {
                appearance: 'none',
            },

            '& label': {
                position: 'absolute',
                width: '100%',
                top: 0,
                color: theme.status.default.color,
                padding: theme.spacing(0.5, 1),
                textAlign: 'center',
                cursor: 'pointer',
            },

            '& input:checked + label': {
                color: theme.palette.common.white,
            },
        },
    });

interface PoolDualButtonProps<T> extends WithStyles<typeof styles> {
    name: string;
    leftButton: PartButtonProps<T>;
    rightButton: PartButtonProps<T>;
    onChange?: (value: T) => void;
    value?: T;
    readonly?: boolean;
}

interface PartButtonProps<T> {
    value: T;
    message: ReactNode;
}

export const PoolDualButton = withStyles(styles)(
    class PoolDualButtonBase<T> extends Component<PoolDualButtonProps<T>> {
        private leftLabel = createRef<HTMLLabelElement>();
        private rightLabel = createRef<HTMLLabelElement>();
        private buttonWidth = 0;
        private buttonIds = [uniqid(), uniqid()];
        private groupId = uniqid();

        public override componentDidMount() {
            // Calculate button width
            if (this.leftLabel.current && this.rightLabel.current) {
                const width =
                    this.leftLabel.current.scrollWidth > this.rightLabel.current.scrollWidth
                        ? this.leftLabel.current.scrollWidth
                        : this.rightLabel.current.scrollWidth;
                this.buttonWidth = width + 8 * 2; // Add left/right padding
                this.forceUpdate();
            }
        }

        private renderLabelContent(message: ReactNode) {
            let labelContent = message;
            if (typeof message === 'string') {
                labelContent = <FormattedMessage id={message} />;
            }
            return labelContent;
        }

        private handleChange = (leftButton: boolean) => () => {
            if (this.props.readonly) {
                return;
            }

            if (this.props.onChange) {
                this.props.onChange(leftButton ? this.props.leftButton.value : this.props.rightButton.value);
            }
        };

        public override render() {
            const { classes, leftButton, rightButton, name, value } = this.props;

            const swapperPosition = this.props.value === this.props.leftButton.value ? '0' : '100%';

            const swapperStyle = {
                width: this.buttonWidth + 'px',
                transform: 'translateX(' + swapperPosition + ')',
            };

            return (
                <div className={classes.container} id={this.groupId}>
                    <div className={classes.flexContainer}>
                        {[leftButton, rightButton].map((button, index) => {
                            const buttonId = this.buttonIds[index];
                            return (
                                <div
                                    key={buttonId}
                                    className={classes.content}
                                    style={{ width: this.buttonWidth + 'px' }}
                                >
                                    <input
                                        type="radio"
                                        name={name}
                                        id={buttonId}
                                        checked={value === button.value}
                                        onChange={this.handleChange(index === 0)}
                                    />
                                    <label htmlFor={buttonId} ref={index === 0 ? this.leftLabel : this.rightLabel}>
                                        {this.renderLabelContent(button.message)}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                    <div className={classes.swapper} style={swapperStyle} />
                </div>
            );
        }
    }
);
